import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
  Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

export interface Category {
  id?: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  isActive?: boolean;
}

class CategoryService {
  private readonly COLLECTION_NAME = 'categories';
  private categoryCache: Map<string, Category> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private allCategoriesCache: Category[] | null = null;
  private allCategoriesCacheExpiry: number = 0;

  // Cache helper methods
  private isCacheValid(expiryTime: number): boolean {
    return Date.now() < expiryTime;
  }

  private setCategoryCache(category: Category): void {
    if (category.id) {
      this.categoryCache.set(category.id, category);
      this.cacheExpiry.set(category.id, Date.now() + this.CACHE_DURATION);
    }
  }

  private getCachedCategory(id: string): Category | null {
    const expiry = this.cacheExpiry.get(id);
    if (expiry && this.isCacheValid(expiry)) {
      return this.categoryCache.get(id) || null;
    }
    // Clean expired cache entry
    this.categoryCache.delete(id);
    this.cacheExpiry.delete(id);
    return null;
  }

  private setAllCategoriesCache(categories: Category[]): void {
    this.allCategoriesCache = categories;
    this.allCategoriesCacheExpiry = Date.now() + this.CACHE_DURATION;
    // Also cache individual categories
    categories.forEach(category => this.setCategoryCache(category));
  }

  private getAllCategoriesFromCache(): Category[] | null {
    if (this.allCategoriesCache && this.isCacheValid(this.allCategoriesCacheExpiry)) {
      return this.allCategoriesCache;
    }
    // Clean expired cache
    this.allCategoriesCache = null;
    this.allCategoriesCacheExpiry = 0;
    return null;
  }

  private invalidateCache(categoryId?: string): void {
    if (categoryId) {
      // Invalidate specific category
      this.categoryCache.delete(categoryId);
      this.cacheExpiry.delete(categoryId);
    }
    // Always invalidate all categories cache when any category changes
    this.allCategoriesCache = null;
    this.allCategoriesCacheExpiry = 0;
  }

  clearCache(): void {
    this.categoryCache.clear();
    this.cacheExpiry.clear();
    this.allCategoriesCache = null;
    this.allCategoriesCacheExpiry = 0;
  }

  async fetchCategories(): Promise<Category[]> {
    // Check cache first
    const cachedCategories = this.getAllCategoriesFromCache();
    if (cachedCategories) {
      return cachedCategories;
    }

    try {
      const q = query(
        collection(db, this.COLLECTION_NAME), 
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const categories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];

      // Cache the results
      this.setAllCategoriesCache(categories);
      
      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories');
    }
  }

  async fetchActiveCategories(): Promise<Category[]> {
    try {
      const categories = await this.fetchCategories();
      return categories.filter(category => category.isActive !== false);
    } catch (error) {
      console.error('Error fetching active categories:', error);
      throw new Error('Failed to fetch active categories');
    }
  }

  async fetchCategoryById(id: string): Promise<Category | null> {
    // Check cache first
    const cachedCategory = this.getCachedCategory(id);
    if (cachedCategory) {
      return cachedCategory;
    }

    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const category = {
          id: docSnap.id,
          ...docSnap.data()
        } as Category;

        // Cache the result
        this.setCategoryCache(category);

        return category;
      }

      return null;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw new Error('Failed to fetch category');
    }
  }

  async fetchCategoryByName(name: string): Promise<Category | null> {
    try {
      // Fetch all categories and find by name
      const categories = await this.fetchCategories();
      return categories.find(cat => cat.name === name) || null;
    } catch (error) {
      console.error('Error fetching category by name:', error);
      throw new Error('Failed to fetch category by name');
    }
  }

  async fetchCategoryByIdOrName(identifier: string): Promise<Category | null> {
    // Try to fetch by ID first
    let category = await this.fetchCategoryById(identifier);

    // If not found, try by name
    if (!category) {
      category = await this.fetchCategoryByName(identifier);
    }

    return category;
  }

  async createCategory(categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...categoryData,
        isActive: categoryData.isActive !== false, // Default to true
        createdAt: now,
        updatedAt: now
      });

      // Invalidate cache since we added a new category
      this.invalidateCache();

      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error('Failed to create category');
    }
  }

  async updateCategory(id: string, categoryData: Partial<Category>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      await updateDoc(docRef, {
        ...categoryData,
        updatedAt: Timestamp.now()
      });

      // Invalidate cache for this specific category and all categories
      this.invalidateCache(id);
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('Failed to update category');
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(docRef);

      // Invalidate cache for this specific category and all categories
      this.invalidateCache(id);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category');
    }
  }

  async toggleCategoryStatus(id: string): Promise<void> {
    try {
      const category = await this.fetchCategoryById(id);
      if (category) {
        await this.updateCategory(id, { isActive: !category.isActive });
      }
    } catch (error) {
      console.error('Error toggling category status:', error);
      throw new Error('Failed to toggle category status');
    }
  }

  subscribeToCategories(callback: (categories: Category[]) => void): () => void {
    const q = query(
      collection(db, this.COLLECTION_NAME), 
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const categories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];

      callback(categories);
    }, (error) => {
      console.error('Error in categories subscription:', error);
    });

    return unsubscribe;
  }

  async seedDefaultCategories(): Promise<void> {
    try {
      const existingCategories = await this.fetchCategories();
      if (existingCategories.length > 0) {
        return; // Categories already exist
      }

      const defaultCategories: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>[] = [
        {
          name: 'Music',
          description: 'Concerts, festivals, and live performances',
          color: '#9333ea',
          icon: '🎵',
          isActive: true
        },
        {
          name: 'Food & Drink',
          description: 'Restaurants, bars, and culinary events',
          color: '#ea580c',
          icon: '🍽️',
          isActive: true
        },
        {
          name: 'Sports',
          description: 'Athletic events and recreational activities',
          color: '#16a34a',
          icon: '⚽',
          isActive: true
        },
        {
          name: 'Arts & Culture',
          description: 'Museums, galleries, and cultural events',
          color: '#dc2626',
          icon: '🎨',
          isActive: true
        },
        {
          name: 'Nightlife',
          description: 'Parties, clubs, and evening entertainment',
          color: '#7c3aed',
          icon: '🌙',
          isActive: true
        },
        {
          name: 'Community',
          description: 'Local gatherings and community events',
          color: '#0891b2',
          icon: '👥',
          isActive: true
        },
        {
          name: 'Health & Wellness',
          description: 'Fitness, yoga, and wellness activities',
          color: '#059669',
          icon: '🧘',
          isActive: true
        },
        {
          name: 'Educational',
          description: 'Workshops, seminars, and learning events',
          color: '#4338ca',
          icon: '📚',
          isActive: true
        }
      ];

      const promises = defaultCategories.map(category => this.createCategory(category));
      await Promise.all(promises);
    } catch (error) {
      console.error('Error seeding default categories:', error);
      throw new Error('Failed to seed default categories');
    }
  }
}

export const categoryService = new CategoryService();